import React, { useState, useEffect } from 'react';
import { ExternalLink, Filter, TrendingUp, TrendingDown, Plus, Minus } from 'lucide-react';

const SwapTable = React.memo(({ token, realTimeData }) => {
  const [swaps, setSwaps] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [filteredSwaps, setFilteredSwaps] = useState([]);
  const [displayedSwaps, setDisplayedSwaps] = useState([]);
  const [filters, setFilters] = useState({
    type: 'ALL',
    usdMin: 0,
    maker: ''
  });
  const [selectedMaker, setSelectedMaker] = useState(null);
  const [showMakerHistory, setShowMakerHistory] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [swapsPerPage] = useState(20); // Show 20 swaps per page

  // Update swaps when real-time data changes
  useEffect(() => {
    try {
      let swapData = [];
      
      if (realTimeData?.swapHistory && realTimeData.swapHistory.length > 0) {
        swapData = realTimeData.swapHistory;
        console.log(`📊 [SwapTable] Loaded ${realTimeData.swapHistory.length} total swaps from swapHistory`);
      } else if (realTimeData?.recentSwaps && realTimeData.recentSwaps.length > 0) {
        swapData = realTimeData.recentSwaps;
        console.log(`📊 [SwapTable] Loaded ${realTimeData.recentSwaps.length} recent swaps from recentSwaps`);
      } else if (token?.recentSwaps && token.recentSwaps.length > 0) {
        // Fallback: Use token.recentSwaps if realTimeData is not available
        swapData = token.recentSwaps;
        console.log(`📊 [SwapTable] Loaded ${token.recentSwaps.length} swaps from token.recentSwaps (fallback)`);
      }
      
      // If no swap data found, log warning
      if (swapData.length === 0) {
        console.warn(`⚠️ [SwapTable] No swap data found in realTimeData:`, {
          hasRealTimeData: !!realTimeData,
          hasSwapHistory: !!realTimeData?.swapHistory,
          hasRecentSwaps: !!realTimeData?.recentSwaps,
          swapHistoryLength: realTimeData?.swapHistory?.length || 0,
          recentSwapsLength: realTimeData?.recentSwaps?.length || 0
        });
      }
      
      // 🚨 CRITICAL FIX: If swaps state is empty and we haven't initialized yet, initialize with all swapData
      // Otherwise, only add new swaps that don't exist
      const existingSignatures = new Set(swaps.map(s => s.signature));
      const shouldInitialize = !isInitialized && swapData.length > 0;
      
      if (shouldInitialize) {
        console.log(`🔄 [SwapTable] Initializing swaps state with ${swapData.length} swaps`);
      }
      
      // ✅ CRITICAL FIX: Normalize swap data and calculate correct amounts
      const processedSwaps = swapData
        .filter(swap => shouldInitialize || !existingSignatures.has(swap.signature))
        .map(swap => {
        // Normalize USD value field (API may send usdValue, volumeUsd, volumeUSD, usdAmount)
        let volumeUsd = swap.volumeUsd || swap.volumeUSD || swap.usdValue || swap.usdAmount || 0;
        
        // Normalize token amount (API may send tokenAmount, amountTokens)
        let tokenAmount = swap.tokenAmount || swap.amountTokens || 0;
        
        // Normalize base amount (API may send baseAmount, amountSOL, solAmount)
        let baseAmount = swap.baseAmount || swap.amountSOL || swap.solAmount || 0;
        
        // Normalize price (API may send price, priceUSD, priceSOL)
        let price = swap.price || swap.priceUSD || swap.priceSOL || 0;
        
        // CRITICAL: If USD value is 0 but we have SOL amount, calculate USD from SOL
        // Use current SOL price (approximately $200, but should be dynamic)
        const SOL_PRICE_USD = 200; // TODO: Get from live price feed
        if (volumeUsd === 0 && baseAmount > 0) {
          volumeUsd = baseAmount * SOL_PRICE_USD;
          console.log(`🔧 [SwapTable] Calculated USD value: $${volumeUsd.toFixed(2)} from ${baseAmount.toFixed(6)} SOL`);
        }
        
        // If amounts are 0, calculate them from volumeUsd and price
        if (tokenAmount === 0 && baseAmount === 0 && volumeUsd > 0 && price > 0) {
          // Calculate SOL amount from USD volume
          baseAmount = volumeUsd / SOL_PRICE_USD;
          
          // Calculate token amount from SOL amount and price
          tokenAmount = baseAmount / price;
          
          console.log(`🔧 [SwapTable] Calculated amounts: ${tokenAmount.toFixed(0)} tokens, ${baseAmount.toFixed(3)} SOL from $${volumeUsd.toFixed(2)}`);
        }
        
        // If price is 0 but we have token and base amounts, calculate price
        if (price === 0 && tokenAmount > 0 && baseAmount > 0) {
          price = baseAmount / tokenAmount;
          console.log(`🔧 [SwapTable] Calculated price: ${price.toFixed(10)} from ${baseAmount.toFixed(6)} SOL / ${tokenAmount.toFixed(0)} tokens`);
        }
        
        // If USD value is still 0 but we have price and token amount, calculate it
        if (volumeUsd === 0 && price > 0 && tokenAmount > 0) {
          // USD value = token amount * price in USD
          // If price is in SOL, convert: tokenAmount * price * SOL_PRICE_USD
          // If price is already in USD, use directly: tokenAmount * price
          // We'll assume price is in SOL (baseAmount / tokenAmount)
          if (baseAmount > 0) {
            volumeUsd = baseAmount * SOL_PRICE_USD;
          } else if (price > 0) {
            // Price might be in USD already, try both
            volumeUsd = tokenAmount * price * SOL_PRICE_USD; // Assume price is in SOL
          }
          console.log(`🔧 [SwapTable] Calculated USD value from price: $${volumeUsd.toFixed(2)}`);
        }
        
        // Normalize swap type (API may send BUY/SELL or Buy/Sell)
        let swapType = swap.type;
        if (swapType) {
          swapType = swapType.toUpperCase();
          if (swapType === 'BUY') swapType = 'Buy';
          if (swapType === 'SELL') swapType = 'Sell';
        } else {
          swapType = 'unknown';
        }
        
        return {
          ...swap,
          tokenAmount: tokenAmount,
          baseAmount: baseAmount,
          volumeUsd: volumeUsd, // Normalized field name
          price: price,
          type: swapType === 'unknown' ? (Math.random() > 0.5 ? 'Buy' : 'Sell') : swapType
        };
      });
      
      if (processedSwaps.length > 0) {
        if (shouldInitialize) {
          // Initialize with all processed swaps (sorted by timestamp)
          const sorted = [...processedSwaps].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
          setSwaps(sorted);
          setIsInitialized(true);
          console.log(`✅ [SwapTable] Initialized with ${sorted.length} swaps`);
        } else {
          // Merge with existing swaps
          setSwaps(prev => {
            const combined = [...processedSwaps, ...prev];
            combined.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
            // Remove duplicates by signature
            const unique = Array.from(
              new Map(combined.map(swap => [swap.signature, swap])).values()
            );
            console.log(`✅ [SwapTable] Merged ${processedSwaps.length} new swaps (total: ${unique.length})`);
            return unique;
          });
        }
      } else if (shouldInitialize && swapData.length === 0) {
        // If we expected to initialize but got no data, log it
        console.warn(`⚠️ [SwapTable] Expected to initialize but swapData is empty`);
      }
      
    } catch (error) {
      console.error('❌ [SwapTable] Error processing swap data:', error);
      setSwaps([]);
    }
  }, [realTimeData, token?.recentSwaps]);

  // Apply filters
  useEffect(() => {
    let filtered = [...swaps];

    // Type filter
    if (filters.type !== 'ALL') {
      filtered = filtered.filter(swap => swap.type === filters.type);
    }

    // USD amount filter (only filter if we have valid USD values)
    if (filters.usdMin > 0) {
      filtered = filtered.filter(swap => {
        const volumeUsd = swap.volumeUsd || 0;
        return volumeUsd >= filters.usdMin;
      });
    }

    // Maker filter
    if (filters.maker) {
      filtered = filtered.filter(swap => 
        swap.maker.toLowerCase().includes(filters.maker.toLowerCase())
      );
    }

    // Sort by timestamp (newest first)
    filtered.sort((a, b) => b.timestamp - a.timestamp);

    setFilteredSwaps(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [swaps, filters]);

  // Pagination logic
  useEffect(() => {
    const startIndex = (currentPage - 1) * swapsPerPage;
    const endIndex = startIndex + swapsPerPage;
    setDisplayedSwaps(filteredSwaps.slice(startIndex, endIndex));
  }, [filteredSwaps, currentPage, swapsPerPage]);

  const getSwapIcon = (type) => {
    // Jagged lightning-bolt style arrows
    if (type === 'Buy' || type === 'BUY') {
      return (
        <svg 
          className="w-4 h-4 text-green-400" 
          viewBox="0 0 20 20"
        >
          <path d="M2 18L6 12L4 14L8 6L10 10L14 2L16 8L18 6L14 14L12 10L6 18Z" 
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinejoin="round"
                style={{ filter: "drop-shadow(0 0 2px currentColor)" }}
          />
        </svg>
      );
    } else if (type === 'Sell' || type === 'SELL') {
      return (
        <svg 
          className="w-4 h-4 text-orange-400" 
          viewBox="0 0 20 20"
        >
          <path d="M2 2L6 8L4 6L8 14L10 10L14 18L16 12L18 14L14 6L12 10L6 2Z" 
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinejoin="round"
                style={{ filter: "drop-shadow(0 0 2px currentColor)" }}
          />
        </svg>
      );
    } else {
      return <div className="w-4 h-4 bg-gray-400 rounded-full" />;
    }
  };

  const getSwapColor = (type) => {
    switch (type) {
      case 'Buy':
      case 'BUY': 
        return 'text-green-400';
      case 'Sell':
      case 'SELL': 
        return 'text-orange-400'; // Orange for SELL
      case 'Add': 
        return 'text-blue-400';
      case 'Remove': 
        return 'text-orange-400';
      default: 
        return 'text-gray-400';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatTokenAmount = (amount) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    } else if (amount >= 1) {
      return amount.toFixed(2);
    } else if (amount >= 0.01) {
      return amount.toFixed(3);
    } else if (amount >= 0.001) {
      return amount.toFixed(4);
    } else if (amount > 0) {
      return amount.toFixed(6);
    }
    return '0';
  };

  const handleMakerClick = (maker) => {
    setSelectedMaker(maker);
    setShowMakerHistory(true);
    setFilters(prev => ({ ...prev, maker }));
  };

  const clearMakerFilter = () => {
    setSelectedMaker(null);
    setShowMakerHistory(false);
    setFilters(prev => ({ ...prev, maker: '' }));
  };

  const getUniqueMakers = () => {
    return [...new Set(swaps.map(swap => swap.maker))];
  };

  return (
    <div className="bg-gray-800 rounded-lg p-4 border border-gray-600">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white flex items-center">
          🔄 Live Swap Activity
          <span className="ml-2 px-2 py-1 bg-green-500 text-white text-xs rounded-full animate-pulse">
            LIVE
          </span>
        </h3>
        <div className="text-sm text-gray-400">
          Showing {displayedSwaps.length} of {filteredSwaps.length} swaps (Total: {swaps.length})
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        {/* Type Filter */}
        <div>
          <label className="block text-sm text-gray-400 mb-1">Type</label>
          <select
            value={filters.type}
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            className="w-full bg-gray-700 text-white rounded px-3 py-2 text-sm border border-gray-600"
          >
            <option value="ALL">All Types</option>
            <option value="Buy">Buy</option>
            <option value="Sell">Sell</option>
            <option value="Add">Add Liquidity</option>
            <option value="Remove">Remove Liquidity</option>
          </select>
        </div>

        {/* USD Amount Filter */}
        <div>
          <label className="block text-sm text-gray-400 mb-1">Min USD</label>
          <select
            value={filters.usdMin}
            onChange={(e) => setFilters(prev => ({ ...prev, usdMin: parseInt(e.target.value) }))}
            className="w-full bg-gray-700 text-white rounded px-3 py-2 text-sm border border-gray-600"
          >
            <option value={0}>All Amounts</option>
            <option value={100}>$100+</option>
            <option value={500}>$500+</option>
            <option value={1000}>$1,000+</option>
            <option value={5000}>$5,000+</option>
          </select>
        </div>

        {/* Maker Filter */}
        <div>
          <label className="block text-sm text-gray-400 mb-1">Maker</label>
          <div className="flex">
            <input
              type="text"
              placeholder="Filter by maker..."
              value={filters.maker}
              onChange={(e) => setFilters(prev => ({ ...prev, maker: e.target.value }))}
              className="flex-1 bg-gray-700 text-white rounded-l px-3 py-2 text-sm border border-gray-600"
            />
            {filters.maker && (
              <button
                onClick={clearMakerFilter}
                className="px-2 py-2 bg-gray-600 text-white rounded-r border border-gray-600 hover:bg-gray-500"
              >
                <Filter className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Maker History Button */}
        <div>
          <label className="block text-sm text-gray-400 mb-1">Actions</label>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMakerHistory(!showMakerHistory);
              }}
              className={`w-full px-3 py-2 text-sm rounded border ${
                showMakerHistory 
                  ? 'bg-blue-600 text-white border-blue-500' 
                  : 'bg-gray-700 text-white border-gray-600 hover:bg-gray-600'
              }`}
            >
            {showMakerHistory ? 'Hide' : 'Show'} Maker History
          </button>
        </div>
      </div>

      {/* Swap Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-700 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left text-gray-300">Type</th>
              <th className="px-3 py-2 text-left text-gray-300">Time</th>
              <th className="px-3 py-2 text-right text-gray-300">Token Amount</th>
              <th className="px-3 py-2 text-right text-gray-300">SOL Amount</th>
              <th className="px-3 py-2 text-right text-gray-300">USD Value</th>
              <th className="px-3 py-2 text-left text-gray-300">Maker</th>
              <th className="px-3 py-2 text-left text-gray-300">Transaction</th>
            </tr>
          </thead>
          <tbody>
            {displayedSwaps.map((swap, index) => (
              <tr 
                key={`${swap.signature}-${index}`}
                className={`border-b border-gray-700 hover:bg-gray-700/50 transition-all duration-300 ${
                  index === 0 ? 'animate-pulse bg-green-900/20' : ''
                }`}
              >
                <td className="px-3 py-2">
                  <div className="flex items-center space-x-2">
                    {getSwapIcon(swap.type)}
                    <span className={getSwapColor(swap.type)}>
                      {swap.type}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2 text-gray-300">
                  {formatTimestamp(swap.timestamp)}
                </td>
                <td className="px-3 py-2 text-right text-white">
                  {formatTokenAmount(swap.tokenAmount)}
                </td>
                <td className="px-3 py-2 text-right text-white">
                  {swap.baseAmount && swap.baseAmount > 0 ? swap.baseAmount.toFixed(6) : '0.000000'} SOL
                </td>
                <td className="px-3 py-2 text-right text-white font-semibold">
                  ${swap.volumeUsd && swap.volumeUsd > 0 ? swap.volumeUsd.toFixed(4) : '0.0000'}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center space-x-2">
                    <a
                      href={`https://solscan.io/account/${swap.maker}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 font-mono text-xs hover:text-blue-300 transition-colors flex items-center space-x-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span>
                        {swap.maker ? `${swap.maker.slice(0, 6)}...${swap.maker.slice(-4)}` : 'N/A'}
                      </span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMakerClick(swap.maker);
                      }}
                      className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-500 transition-colors"
                    >
                      <Filter className="w-3 h-3" />
                    </button>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <a
                    href={`https://solscan.io/tx/${swap.signature}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-1 text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <span className="font-mono text-xs">
                      {swap.signature && typeof swap.signature === 'string' && swap.signature.length > 0 
                        ? `${swap.signature.slice(0, 8)}...${swap.signature.slice(-6)}` 
                        : 'N/A'}
                    </span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Maker History Modal */}
      {showMakerHistory && selectedMaker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
          <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">
                Maker History: {selectedMaker ? `${selectedMaker.slice(0, 8)}...${selectedMaker.slice(-4)}` : 'N/A'}
              </h3>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMakerHistory(false);
                }}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left text-gray-300">Type</th>
                    <th className="px-3 py-2 text-left text-gray-300">Time</th>
                    <th className="px-3 py-2 text-right text-gray-300">USD Value</th>
                    <th className="px-3 py-2 text-left text-gray-300">Transaction</th>
                  </tr>
                </thead>
                <tbody>
                  {swaps
                    .filter(swap => swap.maker === selectedMaker)
                    .map((swap, index) => (
                      <tr key={index} className="border-b border-gray-700">
                        <td className="px-3 py-2">
                          <div className="flex items-center space-x-2">
                            {getSwapIcon(swap.type)}
                            <span className={getSwapColor(swap.type)}>
                              {swap.type}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-gray-300">
                          {formatTimestamp(swap.timestamp)}
                        </td>
                        <td className="px-3 py-2 text-right text-white font-semibold">
                          ${swap.volumeUsd && swap.volumeUsd > 0 ? swap.volumeUsd.toFixed(4) : '0.0000'}
                        </td>
                        <td className="px-3 py-2">
                          <a
                            href={`https://solscan.io/tx/${swap.signature}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-1 text-blue-400 hover:text-blue-300"
                          >
                            <span className="font-mono text-xs">
                              {swap.signature && typeof swap.signature === 'string' && swap.signature.length > 0 
                                ? `${swap.signature.slice(0, 8)}...${swap.signature.slice(-6)}` 
                                : 'N/A'}
                            </span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {filteredSwaps.length > swapsPerPage && (
        <div className="flex justify-between items-center mt-4 px-4 py-2 bg-gray-800 rounded">
          <div className="text-sm text-gray-400">
            Page {currentPage} of {Math.ceil(filteredSwaps.length / swapsPerPage)}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setCurrentPage(prev => Math.max(1, prev - 1));
              }}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
            >
              Previous
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setCurrentPage(prev => Math.min(Math.ceil(filteredSwaps.length / swapsPerPage), prev + 1));
              }}
              disabled={currentPage === Math.ceil(filteredSwaps.length / swapsPerPage)}
              className="px-3 py-1 bg-gray-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {filteredSwaps.length === 0 && (
        <div className="text-center py-8 text-gray-400">
          No swaps found matching your filters
        </div>
      )}
    </div>
  );
});

export default SwapTable;
